import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Form, Input, Button, Selector, 
  Dialog, NavBar, Toast, Popup,
  Tag, Space, Grid, Image, DotLoading,
  SearchBar, List, ImageViewer, Checkbox
} from 'antd-mobile';
import { CameraOutline, CloseCircleOutline, RightOutline } from 'antd-mobile-icons';
import VideoCameraIcon from './VideoCameraIcon';
import { PriceRecord } from '../types';
import api from '../api';
import { getTodayDate } from '../utils';
import { useAuth } from '../context/AuthContext';
import EXIF from 'exif-js';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
}

interface ProductNameOption {
  product_name: string;
  price_num: number;
  price_by_num: number;
}

// 右上角分享按钮
const renderShareButton = (loading: boolean, onShare: () => Promise<void>, isSharedAccess?: boolean) => {
  // 如果是分享用户，不显示分享按钮
  if (isSharedAccess) {
    return null;
  }
  
  return (
    <Button
      size='mini'
      onClick={onShare}
      loading={loading}
      style={{
        fontSize: '12px',
        padding: '2px 8px',
        height: '26px',
        lineHeight: '1'
      }}
    >
      分享
    </Button>
  );
};

const PriceForm: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { shareId: urlShareId } = useParams<{ shareId: string }>();
  const { user, logout } = useAuth();
  const [tagVisible, setTagVisible] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const videoAlbumInputRef = useRef<HTMLInputElement>(null);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [productNameOptions, setProductNameOptions] = useState<ProductNameOption[]>([]);
  const [productNameLoading, setProductNameLoading] = useState(false);
  const [inputProductName, setInputProductName] = useState<string>('');
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [productSearchValue, setProductSearchValue] = useState('');
  const [totalCollectableCount, setTotalCollectableCount] = useState(0);
  const [showSimilarProductSelector, setShowSimilarProductSelector] = useState(false);
  const [similarProductOptions, setSimilarProductOptions] = useState<string[]>([]);
  const [isSharedAccess, setIsSharedAccess] = useState(false);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [sourceType, setSourceType] = useState<string>('');
  const [sourceDetail, setSourceDetail] = useState<string>('');
  const [sourceDetailOptions, setSourceDetailOptions] = useState<{label: string, value: string}[]>([]);
  const [sourceDetailLoading, setSourceDetailLoading] = useState(false);

  // 加载可采集商品数据和检查分享状态
  useEffect(() => {
    // 检查分享状态的函数
    const checkShareStatus = () => {
      const storedShareId = localStorage.getItem('current_share_id');
      setIsSharedAccess(!!storedShareId);
    };
    
    // 初始检查
    checkShareStatus();
    
    const category = form.getFieldValue('category');
    if (category) {
      loadProductNameOptions();
    }
    
    // 加载总的可采集商品数量
    fetchTotalCollectableCount();

    // 添加storage事件监听，处理其他页面对localStorage的修改
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'current_share_id') {
        checkShareStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // 设置定时器在组件挂载后再次检查，确保捕获到SharedRoute的状态更新
    const timer = setTimeout(checkShareStatus, 500);

    // 组件卸载时清理
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearTimeout(timer);
    };
  }, []);

  // 加载数据
  useEffect(() => {
    // 处理编辑状态
    const editId = localStorage.getItem('editingId');
    if (editId) {
      setLoading(true);
      setEditingId(editId);
      
      // 清空当前状态
      setMediaItems([]);
      setTags([]);
      
      // 从API获取记录详情
      api.getPriceDetail(editId)
        .then((response: {success: boolean; data?: PriceRecord; error?: string}) => {
          if (response.success && response.data) {
            const record = response.data;
            form.setFieldsValue({
              category: record.category,
              productName: record.productName,
              listPrice: record.listPrice,
              finalPrice: record.finalPrice,
              location: record.location,
              date: record.date,
              similarProductName: record.similarProductName,
              weight: record.weight,
              source: record.source,
              isMatched: record.isMatched === 1
            });
            
            // 设置信息来源类型
            if (record.source) {
              setSourceType(record.source);
              // 如果有详细来源，则设置详细来源
              if (record.sourceDetail) {
                setSourceDetail(record.sourceDetail);
                form.setFieldsValue({ sourceDetail: record.sourceDetail });
              }
              // 加载二级信息来源选项
              loadSourceDetailOptions(record.source);
            }
            
            setTags(Array.isArray(record.tags) ? record.tags : []);
            
            // 加载图片文件
            if (record.mediaUrls && record.mediaUrls.length > 0) {
              const items: MediaItem[] = record.mediaUrls.map((url: string) => {
                // 根据URL后缀判断媒体类型
                const isVideo = /\.(mp4|mov|webm|ogg|avi|wmv|flv|mkv)$/i.test(url);
                return {
                  url,
                  type: isVideo ? 'video' : 'image'
                };
              });
              setMediaItems(items);
            } else {
              setMediaItems([]);
            }
            
            // 同步本地输入状态
            setInputProductName(record.productName || '');
          } else {
            Toast.show({
              icon: 'fail',
              content: '加载记录失败',
            });
            navigate('/list');
          }
        })
        .catch((error: Error) => {
          Toast.show({
            icon: 'fail',
            content: '加载记录失败',
          });
          navigate('/list');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // 默认值设置
      form.resetFields();
      setTags([]);
      setMediaItems([]);
      setInputProductName(''); 
      
      // 获取最近使用的地点和记住的品类、信息来源
      let lastLocation = localStorage.getItem('lastLocation') || '';
      const lastCategory = localStorage.getItem('lastCategory') || '牛肉';
      const lastSource = localStorage.getItem('lastSource') || '线上';
      const lastSourceDetail = localStorage.getItem('lastSourceDetail') || '';
      
      // 获取之前保存的信息来源
      setSourceType(lastSource);

      // 如果信息来源是线上，则地点为线上
      if(lastSource ==="线上"){
        lastLocation = '线上';
      }

      // 设置之前保存的二级信息来源
      if (lastSourceDetail) {
        setSourceDetail(lastSourceDetail);
      }
      
      form.setFieldsValue({
        date: getTodayDate(),
        location: lastLocation,
        category: lastCategory,
        source: lastSource,
        sourceDetail: lastSourceDetail
      });
      
      // 加载默认的二级信息来源选项
      loadSourceDetailOptions(lastSource);
    }
  }, [form, navigate]);

  // 获取相似商品历史记录
  useEffect(() => {
    const recentSimilarProducts = localStorage.getItem('recentSimilarProducts');
    if (recentSimilarProducts) {
      try {
        const parsedOptions = JSON.parse(recentSimilarProducts);
        if (Array.isArray(parsedOptions)) {
          setSimilarProductOptions(parsedOptions);
        }
      } catch (e) {
        console.error('解析相似商品历史记录失败:', e);
        setSimilarProductOptions([]);
      }
    }
  }, []);

  // 直接调起相机拍照
  const captureMedia = () => {
    Dialog.show({
      content: '请选择图片来源',
      closeOnAction: true,
      actions: [
        {
          key: 'camera',
          text: '拍照',
          onClick: () => {
            if (fileInputRef.current) {
              fileInputRef.current.setAttribute('capture', 'environment');
              fileInputRef.current.click();
            }
          }
        },
        {
          key: 'album',
          text: '从相册选择',
          onClick: () => {
            if (fileInputRef.current) {
              fileInputRef.current.removeAttribute('capture');
              fileInputRef.current.click();
            }
          }
        },
        {
          key: 'cancel',
          text: '取消',
          style: { color: '#999' }
        }
      ]
    });
  };

  // 录像入口
  const captureVideo = () => {
    Dialog.show({
      content: '请选择视频来源',
      closeOnAction: true,
      actions: [
        {
          key: 'camera',
          text: '录像',
          onClick: () => {
            if (videoInputRef.current) {
              videoInputRef.current.click();
            }
          }
        },
        {
          key: 'album',
          text: '从相册选择',
          onClick: () => {
            if (videoAlbumInputRef.current) {
              videoAlbumInputRef.current.click();
            }
          }
        },
        {
          key: 'cancel',
          text: '取消',
          style: { color: '#999' }
        }
      ]
    });
  };

  // 处理图片或视频文件
  const handleMediaCapture = async (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' = 'image') => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // 只对图片做EXIF校验
      if (type === 'image') {
        // 判断是否为相册选择（没有 capture 属性）
        const isAlbum = !(fileInputRef.current && fileInputRef.current.hasAttribute('capture'));
        // 读取EXIF并校验
        try {
          await new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = function(event) {
              try {
                const result = event.target?.result;
                if (!result) {
                  reject(new Error('无法读取图片数据'));
                  return;
                }
                const exifData = EXIF.readFromBinaryFile(result as ArrayBuffer);
                const exifDate = exifData.DateTimeOriginal;
                const gpsLat = exifData.GPSLatitude;
                const gpsLatRef = exifData.GPSLatitudeRef;
                const gpsLon = exifData.GPSLongitude;
                const gpsLonRef = exifData.GPSLongitudeRef;
                // 校验拍摄日期
                if (isAlbum) {
                  let validDate = false;
                  if (exifDate) {
                    // exifDate 格式为 "YYYY:MM:DD HH:MM:SS"
                    const dateStr = exifDate.split(' ')[0].replace(/:/g, '-');
                    const todayStr = new Date().toISOString().slice(0, 10);
                    if (dateStr === todayStr) {
                      validDate = true;
                    }
                  }
                  if (!validDate) {
                    // 尝试用文件的lastModified时间
                    const fileDate = new Date(file.lastModified);
                    const fileDateStr = fileDate.toISOString().slice(0, 10);
                    const todayStr = new Date().toISOString().slice(0, 10);
                    if (fileDateStr === todayStr) {
                      validDate = true;
                    }
                  }
                  if (!validDate) {
                    reject(new Error('只能上传当天拍摄的照片'));
                    return;
                  }
                }
                // 解析地理位置
                let lat = null, lon = null;
                if (gpsLat && gpsLon && gpsLatRef && gpsLonRef) {
                  lat = gpsLat[0] + gpsLat[1] / 60 + gpsLat[2] / 3600;
                  if (gpsLatRef === 'S') lat = -lat;
                  lon = gpsLon[0] + gpsLon[1] / 60 + gpsLon[2] / 3600;
                  if (gpsLonRef === 'W') lon = -lon;
                  console.log('照片地理位置:', lat, lon);
                } else {
                  console.log('照片无地理位置信息');
                }
                resolve();
              } catch (err) {
                reject(err);
              }
            };
            reader.onerror = function() {
              reject(new Error('读取图片文件失败'));
            };
            reader.readAsArrayBuffer(file);
          });
        } catch (err) {
          Toast.show({ content: (err as Error).message || '照片校验失败', icon: 'fail' });
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        // 校验通过后，才添加"上传中"占位图
        const uploadingIndex = mediaItems.length;
        const tempUrl = URL.createObjectURL(file);
        const tempItem = { url: tempUrl, type, isUploading: true };
        setMediaItems(prevItems => [...prevItems, tempItem as any]);
        // 检查文件大小，限制为50MB
        const maxSize = 50 * 1024 * 1024; // 50MB
        if (file.size > maxSize) {
          setMediaItems(prevItems => prevItems.filter((_, i) => i !== uploadingIndex));
          Toast.show({
            icon: 'fail',
            content: `文件大小超过限制（最大50MB）`,
            duration: 2000,
            position: 'bottom'
          });
          if (fileInputRef.current) fileInputRef.current.value = '';
          URL.revokeObjectURL(tempUrl);
          return;
        }
        // 上传文件到服务器
        const result = await api.uploadMedia([file]);
        if (result.success && result.data && result.data.urls && result.data.urls.length > 0) {
          const fileUrl = result.data.urls[0];
          setMediaItems(prevItems => 
            prevItems.map((item, index) => 
              index === uploadingIndex ? { url: fileUrl, type } : item
            )
          );
          Toast.show({
            icon: 'success',
            content: '照片上传成功',
            duration: 1500,
            position: 'bottom',
          });
        } else {
          setMediaItems(prevItems => prevItems.filter((_, i) => i !== uploadingIndex));
          Toast.show({
            icon: 'fail',
            content: `照片上传失败: ${result.error || '服务器错误'}`,
            position: 'bottom'
          });
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      
      // 处理视频文件
      if (type === 'video') {
        // 添加"上传中"占位视频
        const uploadingIndex = mediaItems.length;
        const tempUrl = URL.createObjectURL(file);
        const tempItem = { url: tempUrl, type, isUploading: true };
        setMediaItems(prevItems => [...prevItems, tempItem as any]);
        
        // 检查文件大小，限制为100MB
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
          setMediaItems(prevItems => prevItems.filter((_, i) => i !== uploadingIndex));
          Toast.show({
            icon: 'fail',
            content: `视频文件大小超过限制（最大100MB）`,
            duration: 2000,
            position: 'bottom'
          });
          if (videoInputRef.current) videoInputRef.current.value = '';
          if (videoAlbumInputRef.current) videoAlbumInputRef.current.value = '';
          URL.revokeObjectURL(tempUrl);
          return;
        }
        
        // 上传视频文件到服务器
        try {
          const result = await api.uploadMedia([file]);
          if (result.success && result.data && result.data.urls && result.data.urls.length > 0) {
            const fileUrl = result.data.urls[0];
            setMediaItems(prevItems => 
              prevItems.map((item, index) => 
                index === uploadingIndex ? { url: fileUrl, type } : item
              )
            );
            Toast.show({
              icon: 'success',
              content: '视频上传成功',
              duration: 1500,
              position: 'bottom',
            });
          } else {
            setMediaItems(prevItems => prevItems.filter((_, i) => i !== uploadingIndex));
            Toast.show({
              icon: 'fail',
              content: `视频上传失败: ${result.error || '服务器错误'}`,
              position: 'bottom'
            });
          }
        } catch (error) {
          setMediaItems(prevItems => prevItems.filter((_, i) => i !== uploadingIndex));
          Toast.show({
            icon: 'fail',
            content: '视频上传失败: 网络错误',
            position: 'bottom'
          });
        }
        
        // 清空输入框
        if (videoInputRef.current) videoInputRef.current.value = '';
        if (videoAlbumInputRef.current) videoAlbumInputRef.current.value = '';
        URL.revokeObjectURL(tempUrl);
        return;
      }
    }
  };

  // 删除图片项
  const removeMediaItem = (index: number) => {
    // 检查是否正在上传
    if ((mediaItems[index] as any).isUploading) {
      Toast.show({
        content: '上传中，无法删除',
        position: 'bottom'
      });
      return;
    }
    
    const newItems = [...mediaItems];
    newItems.splice(index, 1);
    setMediaItems(newItems);
  };

  // 预览图片
  const previewMedia = (url: string) => {
    // 查找当前项
    const mediaItem = mediaItems.find(item => item.url === url);
    
    // 如果正在上传中，不允许预览
    if (mediaItem && (mediaItem as any).isUploading) {
      return;
    }
    
    // 如果是视频，使用现有预览
    if (mediaItem?.type === 'video') {
      setPreviewUrl(url);
      setPreviewVisible(true);
      return;
    }
    
    // 如果是图片，使用ImageViewer
    // 准备所有图片的URL数组
    const imageUrls = mediaItems
      .filter(item => item.type === 'image')
      .map(item => item.url);
    
    // 找到当前图片在数组中的索引
    const index = imageUrls.findIndex(imgUrl => imgUrl === url);
    if (index !== -1) {
      setCurrentImageIndex(index);
      setImageViewerVisible(true);
    }
  };

  // 表单提交处理
  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      // 构建要保存的数据
      const formData: PriceRecord = {
        ...values,
        listPrice: values.listPrice || null,
        tags: Array.isArray(tags) ? tags : [],
        mediaUrls: mediaItems.map(item => item.url),
        employeeId: user?.employeeId.toString()+"-"+user?.realname,
        sourceDetail: values.sourceDetail,
        isMatched: values.isMatched ? 1 : 3
      };

      console.log('表单数据:', formData);
      
      // 保存最近使用的地点
      if (values.location) {
        localStorage.setItem('lastLocation', values.location);
      }
      
      // 保存相似商品到历史记录
      if (values.similarProductName) {
        saveSimilarProductToLocalStorage(values.similarProductName);
      }
      
      let result;
      if (editingId) {
        // 更新现有记录
        console.log('正在更新记录, ID:', editingId);
        result = await api.updatePrice(editingId, formData);
      } else {
        // 添加新记录
        result = await api.createPrice(formData);
      }

      console.log('API响应:', result);
      
      if (result.success) {
        Toast.show({
          icon: 'success',
          content: '保存成功',
        });
        
        // 保存成功后清除编辑状态
        localStorage.removeItem('editingId');
        
        // 保存当前选择的品类和信息来源
        if (values.category) {
          localStorage.setItem('lastCategory', values.category);
        }
        if (values.source) {
          localStorage.setItem('lastSource', values.source);
        }
        
        // 重新加载可采集数量
        fetchTotalCollectableCount();
        
        resetForm();
        
        // 如果是编辑模式，可以选择跳转回列表
        if (editingId) {
          navigate('/list');
        }
      } else {
        Toast.show({
          icon: 'fail',
          content: `保存失败: ${result.error || '服务器错误'}`,
          duration: 3000,
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '提交失败';
      console.error('表单提交错误:', errorMessage);
      
      Toast.show({
        icon: 'fail',
        content: `保存失败: ${errorMessage}`,
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // 重置表单
  const resetForm = () => {
    // 保存当前品类和信息来源
    const currentCategory = form.getFieldValue('category');
    const currentSource = form.getFieldValue('source');
    const currentSourceDetail = form.getFieldValue('sourceDetail');
    
    // 记住选择的值
    if (currentCategory) {
      localStorage.setItem('lastCategory', currentCategory);
    }
    if (currentSource) {
      localStorage.setItem('lastSource', currentSource);
    }
    if (currentSourceDetail) {
      localStorage.setItem('lastSourceDetail', currentSourceDetail);
    }
    
    form.resetFields();
    setTags([]);
    setEditingId(null);
    setMediaItems([]);
    setInputProductName('');
    localStorage.removeItem('editingId');
    
    // 获取最近使用的地点和记住的品类、信息来源
    const lastLocation = localStorage.getItem('lastLocation') || '';
    const lastCategory = localStorage.getItem('lastCategory') || '牛肉';
    const lastSource = localStorage.getItem('lastSource') || '线上';
    const lastSourceDetail = localStorage.getItem('lastSourceDetail') || '';
    
    form.setFieldsValue({
      date: getTodayDate(),
      location: lastLocation,
      category: lastCategory, // 使用记住的品类，如果没有则默认为牛肉
      source: lastSource, // 使用记住的信息来源，如果没有则默认为线上
      sourceDetail: lastSourceDetail
    });
    
    // 加载默认的二级信息来源选项
    loadSourceDetailOptions(lastSource);
  };

  // 添加标签
  const addTag = () => {
    if (newTag.trim()) {
      const currentTags = Array.isArray(tags) ? tags : [];
      setTags([...currentTags, newTag.trim()]);
      setNewTag('');
      setTagVisible(false);
    }
  };

  // 删除标签
  const removeTag = (index: number) => {
    if (!Array.isArray(tags)) return;
    
    const newTags = [...tags];
    newTags.splice(index, 1);
    setTags(newTags);
  };

  // 处理品类变更
  const handleCategoryChange = (val: string) => {
    form.setFieldsValue({ productName: '' });
    setInputProductName('');
    
    // 保存选择的品类
    localStorage.setItem('lastCategory', val);
    
    loadProductNameOptions();
    // 重新加载可采集数量
    fetchTotalCollectableCount();
  };

  // 加载商品名称列表数据
  const loadProductNameOptions = async () => {
    const category = form.getFieldValue('category');
    if (!category) {
      Toast.show({
        content: '请先选择品类',
        position: 'bottom',
      });
      return;
    }

    setProductNameLoading(true);
    setProductSearchValue('');
    try {
      const res = await api.getDefinedDataRecords(category);
      if (res.success && Array.isArray(res.data)) {
        const options = res.data.map((item: any) => ({
          product_name: item.product_name,
          price_num: item.num-item.price_num>0 && item.price_by_num === 0? 1:0
        }));
        setProductNameOptions(options);
      } else {
        setProductNameOptions([]);
      }
    } catch (error) {
      console.error('加载商品名称数据失败:', error);
      setProductNameOptions([]);
      Toast.show({
        content: '加载商品名称失败',
        position: 'bottom',
      });
    } finally {
      setProductNameLoading(false);
    }
  };

  // 获取总的可采集商品数量
  const fetchTotalCollectableCount = async () => {
    try {
      const res = await api.getCollectableCount();
      if (res.success && res.data && res.data.num !== undefined) {
        setTotalCollectableCount(res.data.num);
      } else {
        setTotalCollectableCount(0);
      }
    } catch (error) {
      console.error('获取可采集商品总数失败:', error);
      setTotalCollectableCount(0);
    }
  };

  // 处理点击商品名称区域
  const handleProductNameClick = () => {
    loadProductNameOptions();
    setShowProductSelector(true);
  };

  // 处理商品名称更新
  const handleProductNameChange = (value: string) => {
    setInputProductName(value);
    form.setFieldsValue({ productName: value });
  };

  // 处理选择商品名称
  const handleSelectProductName = (value: string) => {
    setInputProductName(value);
    form.setFieldsValue({ productName: value });
    setShowProductSelector(false);
  };

  // 过滤商品列表
  const getFilteredProducts = () => {
    if (!productSearchValue) return productNameOptions;
    return productNameOptions.filter(opt => opt.product_name.includes(productSearchValue));
  };

  // 添加meta viewport设置，防止输入框获得焦点时缩放
  useEffect(() => {
    // 查找现有的viewport meta标签
    let viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
    
    // 如果不存在则创建一个新的
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }
    
    // 设置meta标签内容，防止缩放
    viewportMeta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
    
    // 组件卸载时移除设置
    return () => {
      if (viewportMeta) {
        viewportMeta.content = 'width=device-width, initial-scale=1';
      }
    };
  }, []);

  // 处理信息来源类型变更
  const handleSourceTypeChange = (val: string[]) => {
    if (val.length > 0) {
      const sourceValue = val[0];
      setSourceType(sourceValue);
      // 保存信息来源类型
      localStorage.setItem('lastSource', sourceValue);
      // 清空之前选择的详细来源
      setSourceDetail('');
      // 地点自动设置逻辑
      if (sourceValue === '线上') {
        form.setFieldsValue({ 
          source: sourceValue,
          sourceDetail: '',
          location: '线上'
        });
      } else if (sourceValue === '线下') {
        form.setFieldsValue({ 
          source: sourceValue,
          sourceDetail: '',
          location: ''
        });
      } else {
        form.setFieldsValue({ 
          source: sourceValue,
          sourceDetail: ''
        });
      }
      // 加载二级信息来源选项
      loadSourceDetailOptions(sourceValue);
    }
  };

  // 加载二级信息来源选项
  const loadSourceDetailOptions = async (source: string) => {
    if (!source) return;
    
    setSourceDetailLoading(true);
    try {
      const res = await api.getDefinedData(source);
      if (res.success && Array.isArray(res.data)) {
        const options = res.data.map((item: {defined_key: string}) => ({
          label: item.defined_key,
          value: item.defined_key
        }));
        setSourceDetailOptions(options);
      } else {
        // 如果API调用失败，设置为空数组
        setSourceDetailOptions([]);
      }
    } catch (error) {
      console.error('加载二级信息来源选项失败:', error);
      // 出错时设置为空数组
      setSourceDetailOptions([]);
    } finally {
      setSourceDetailLoading(false);
    }
  };

  // 处理信息来源详情变更
  const handleSourceDetailChange = (val: string[]) => {
    if (val.length > 0) {
      const detailValue = val[0];
      setSourceDetail(detailValue);
      
      // 保存最后一次选择的二级信息来源到localStorage
      localStorage.setItem('lastSourceDetail', detailValue);
      
      // 更新表单值
      form.setFieldsValue({ sourceDetail: detailValue });
    }
  };

  // 保存相似商品到本地存储
  const saveSimilarProductToLocalStorage = (productName: string) => {
    if (!productName) return;
    
    try {
      // 获取现有记录
      let recentProducts = localStorage.getItem('recentSimilarProducts');
      let products: string[] = [];
      
      if (recentProducts) {
        products = JSON.parse(recentProducts);
      }
      
      // 移除已存在的相同项
      products = products.filter(item => item !== productName);
      
      // 添加到最前面
      products.unshift(productName);
      
      // 只保留最近20条
      products = products.slice(0, 20);
      
      // 保存回本地存储
      localStorage.setItem('recentSimilarProducts', JSON.stringify(products));
      
      // 更新状态
      setSimilarProductOptions(products);
    } catch (e) {
      console.error('保存相似商品历史记录失败:', e);
    }
  };

  // 处理分享按钮点击
  const handleShare = async () => {
    try {
      setSharingLoading(true);
      
      // 调用API生成分享链接
      const result = await api.generateShareLink();
      
      if (result.success && result.data?.shareId) {
        const shareId = result.data.shareId;
        const shareLink = `${window.location.origin}/share/${shareId}`;
        
        // 尝试复制链接到剪贴板
        let copySuccess = false;
        
        // 针对iOS设备优化的复制功能
        const copyToClipboardFallback = (text: string) => {
          // 创建一个临时的textarea元素
          const textArea = document.createElement('textarea');
          textArea.value = text;
          
          // 设置样式使其不可见
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          
          // 聚焦并选择文本
          textArea.focus();
          textArea.select();
          
          // 尝试使用document.execCommand('copy')复制
          try {
            const successful = document.execCommand('copy');
            copySuccess = successful;
          } catch (err) {
            console.error('Fallback: 复制到剪贴板失败', err);
            copySuccess = false;
          }
          
          // 清理
          document.body.removeChild(textArea);
        };
        
        // 首先尝试使用现代API
        try {
          await navigator.clipboard.writeText(shareLink);
          copySuccess = true;
        } catch (err) {
          console.warn('Clipboard API失败，尝试兼容方式', err);
          // 如果现代API失败，尝试兼容方式
          copyToClipboardFallback(shareLink);
        }
        
        if (copySuccess) {
          Toast.show({
            icon: 'success',
            content: '链接已复制到剪贴板',
            position: 'bottom',
            duration: 2000,
          });
        } else {
          // 显示链接给用户手动复制
          Dialog.alert({
            content: (
              <div>
                <p>请手动复制以下链接：</p>
                <div style={{ 
                  padding: '10px', 
                  background: '#f5f5f5', 
                  borderRadius: '4px',
                  wordBreak: 'break-all',
                  marginTop: '8px',
                  userSelect: 'all' // 使文本可选中
                }}>
                  {shareLink}
                </div>
              </div>
            ),
            confirmText: '确定',
          });
        }
      } else {
        Toast.show({
          icon: 'fail',
          content: result.error || '生成分享链接失败',
          position: 'bottom',
        });
      }
    } catch (error) {
      console.error('分享错误:', error);
      Toast.show({
        icon: 'fail',
        content: '分享失败，请稍后重试',
        position: 'bottom',
      });
    } finally {
      setSharingLoading(false);
    }
  };

  // 导航栏右侧区域
  const renderRight = () => {
    // 如果是分享访问模式，不显示分享按钮
    return renderShareButton(sharingLoading, handleShare, isSharedAccess);
  };

  return (
    <div style={{ 
      maxWidth: '600px', 
      margin: '0 auto', 
      padding: '0 12px', 
      paddingBottom: '80px', 
      position: 'relative' 
    }}>
      <NavBar
        back={null}
        left={
          user && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button 
                size='mini' 
                onClick={() => {
                  Dialog.confirm({
                    content: '确定要退出登录吗？',
                    onConfirm: () => {
                      localStorage.removeItem('current_share_id');
                      logout();
                      navigate('/login');
                    }
                  });
                }}
                style={{ 
                  marginRight: '6px',
                  fontSize: '12px',
                  padding: '2px 8px',
                  height: '24px',
                  lineHeight: '1'
                }}
              >
                退出
              </Button>
              <span style={{ 
                fontSize: '13px', 
                color: '#666',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100px'
              }}>
                {user?.realname}
                {isSharedAccess && (
                  <span style={{ marginLeft: '6px', fontSize: '12px', height: '18px', lineHeight: '16px', backgroundColor: '#ff4d4f', color: '#fff', padding: '0 4px', borderRadius: '4px' }}>
                    分享
                  </span>
                )}
              </span>
            </div>
          )
        }
        right={renderRight()}
        style={{ fontSize: '18px' }}
      >
        市场价格采集
      </NavBar>
      
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '40px' 
        }}>
          <DotLoading color='primary' />
          <span style={{ marginLeft: 8 }}>数据加载中...</span>
        </div>
      ) : (
        <Form
          form={form}
          layout='horizontal'
          onFinish={onFinish}
          disabled={loading}
          style={{
            '--prefix-width': '4.5em',
            '--form-item-label-width': '4.5em',
            '--form-item-content-padding-left': '0',
            '--form-item-margin-bottom': '5px',
            '--form-item-font-size': '16px',
            paddingBottom: '20px'
          } as any}
        >
          <Form.Header>
            <div style={{ 
              fontSize: '16px', 
              margin: '2px 0 4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              
              <span style={{ 
                color: '#ff4d4f', 
                fontSize: '20px',
                fontWeight: 'normal'
              }}>
                {totalCollectableCount > 0 ? `${totalCollectableCount}个商品可采集` : ''}
              </span>
            </div>
          </Form.Header>

          <Form.Item 
            name='source' 
            label='信息来源'
          >
            <Selector
              options={[
                { label: '线上', value: '线上' },
                { label: '线下', value: '线下' },
              ]}
              multiple={false}
              value={sourceType ? [sourceType] : []}
              onChange={val => handleSourceTypeChange(val)}
            />
          </Form.Item>

          {/* 二级信息来源选项 */}
          {sourceType && (
            <Form.Item 
              name='sourceDetail' 
              label='渠道'
            >
              {sourceDetailLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
                  <DotLoading color='primary' />
                  <span style={{ marginLeft: 8 }}>加载中...</span>
                </div>
              ) : (
                <Selector
                  options={sourceDetailOptions}
                  multiple={false}
                  value={sourceDetail ? [sourceDetail] : []}
                  onChange={val => handleSourceDetailChange(val)}
                />
              )}
            </Form.Item>
          )}

          <Form.Header>
            <div style={{ fontSize: '16px', margin: '1px 0 1px' }}>照片/视频</div>
          </Form.Header>
          <Form.Item name='media'>
            <div style={{ padding: '0 12px 1px' }}>
              <Grid columns={4} gap={8}>
                {mediaItems.map((item, index) => (
                  <Grid.Item key={index}>
                    <div style={{ position: 'relative', margin: '6px 4px' }}>
                      <div 
                        onClick={() => previewMedia(item.url)}
                        style={{ 
                          width: '100%', 
                          height: 80, 
                          borderRadius: 4, 
                          overflow: 'hidden', 
                          background: '#000',
                          border: '1px solid #eee'
                        }}
                      >
                        {(item as any).isUploading ? (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(0,0,0,0.5)'
                          }}>
                            <DotLoading color='white' />
                          </div>
                        ) : item.type === 'image' ? (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              backgroundImage: `url(${item.url})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                        ) : (
                          <video
                            src={item.url}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            muted
                            playsInline
                          />
                        )}
                      </div>
                      <Button
                        style={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          padding: 0,
                          borderRadius: '50%',
                          minWidth: 'unset',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          zIndex: 10,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          border: '1px solid #fff'
                        }}
                        color='danger'
                        onClick={(e) => {
                          e.stopPropagation();
                          removeMediaItem(index);
                        }}
                        disabled={(item as any).isUploading}
                      >
                        <CloseCircleOutline fontSize={16} />
                      </Button>
                    </div>
                  </Grid.Item>
                ))}
              </Grid>
              {/* 添加按钮单独一行，避免遮挡 */}
              {mediaItems.length < 9 && (
                <div style={{ marginTop: 1, marginBottom: 10, display: 'flex', justifyContent: 'flex-start' }}>
                  <div
                    style={{ 
                      background: 'none',
                      display: 'flex', 
                      flexDirection: 'row',
                      gap: 20,
                      width: '100%'
                    }}
                  >
                    <div
                      onClick={() => !loading && captureMedia()}
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minWidth: 90,
                        height: 90,
                        background: '#fafbfc',
                        border: '1.5px dashed #d9d9d9',
                        borderRadius: 8,
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        transition: 'box-shadow 0.2s',
                      }}
                    >
                      <CameraOutline style={{ fontSize: 32, color: '#999' }} />
                      <span style={{ fontSize: 15, color: '#666', marginTop: 8 }}>拍照</span>
                    </div>
                    <div
                      onClick={() => !loading && captureVideo()}
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minWidth: 90,
                        height: 90,
                        background: '#fafbfc',
                        border: '1.5px dashed #d9d9d9',
                        borderRadius: 8,
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        transition: 'box-shadow 0.2s',
                      }}
                    >
                      <VideoCameraIcon size={32} color="#999" />
                      <span style={{ fontSize: 15, color: '#666', marginTop: 8 }}>录像</span>
                    </div>
                  </div>
                </div>
              )}
              <div style={{ 
                marginTop: 0, 
                marginBottom: 1, 
                fontSize: 13, 
                color: 'red', 
                textAlign: 'left' 
              }}>
                拍照录像要能清晰的看到，商品包装和价格信息。
              </div>
            </div>
            <input 
              ref={fileInputRef}
              type="file" 
              style={{ display: 'none' }}
              accept="image/*"
              onChange={e => handleMediaCapture(e, 'image')}
              disabled={loading}
            />
            <input
              ref={videoInputRef}
              type="file"
              style={{ display: 'none' }}
              accept="video/*"
              capture="environment"
              onChange={e => handleMediaCapture(e, 'video')}
              disabled={loading}
            />
            <input
              ref={videoAlbumInputRef}
              type="file"
              style={{ display: 'none' }}
              accept="video/*"
              onChange={e => handleMediaCapture(e, 'video')}
              disabled={loading}
            />
          </Form.Item>

          <Form.Item 
            name='category' 
            label='品类' 
            rules={[{ required: true, message: '请选择品类' }]}
          >
            <Selector
              options={[
                { label: '牛肉', value: '牛肉' },
                { label: '红酒', value: '红酒' }
              ]}
              onChange={val => handleCategoryChange(val[0])}
            />
          </Form.Item>

          <Form.Item 
            name='productName' 
            label='商品名称'
            rules={[{ required: true, message: '请输入商品名称' }]}
          >
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '0 4px',
                background: '#fff',
                position: 'relative',
                minHeight: '42px'
              }}
              onClick={handleProductNameClick}
            >
              <div style={{
                flex: '1 1 auto',
                padding: '8px 20px 8px 4px',
                whiteSpace: 'normal',
                wordBreak: 'break-all',
                lineHeight: '1.2',
                fontSize: '16px'
              }}>
                {inputProductName || <span style={{ color: '#999'}}>选择公司商品</span>}
              </div>
              <RightOutline style={{ color: '#999', position: 'absolute', right: '6px' }} />
            </div>
          </Form.Item>

          <Form.Item 
            label='外部商品'
          >
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
              <Form.Item 
                name='similarProductName' 
                noStyle
              >
                <Input 
                  placeholder='选择或输入外部商品'
                  style={{ 
                    fontSize: '16px',
                    flex: 1
                  }}
                  onChange={(e) => {
                    setTimeout(() => form.validateFields(['weight']), 100);
                  }}
                />
              </Form.Item>
              <div 
                onClick={() => setShowSimilarProductSelector(true)}
                style={{
                  position: 'absolute',
                  right: '8px',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <RightOutline style={{ color: '#999' }} />
              </div>
            </div>
          </Form.Item>
  
          <Form.Item name='isMatched' label=' '>
            <Checkbox>采集商品跟公司商品完全匹配</Checkbox>
          </Form.Item> 
          <Form.Item 
            name='weight' 
            label='克重/ml'
            rules={[{ required: true, message: '请输入克重或毫升数' }]}
          >
            <Input 
              type='number' 
              placeholder='输入克重或毫升数'
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item name='listPrice' label='标价'>
            <Input 
              type='number' 
              placeholder='选填'
              style={{ fontSize: '16px' }} 
            />
          </Form.Item>

          <Form.Item 
            name='finalPrice' 
            label='到手价'
            rules={[{ required: true, message: '请输入到手价' }]}
          >
            <Input 
              type='number' 
              placeholder='请输入价格'
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item 
            name='location' 
            label='地点'
            rules={[{ required: true, message: '请输入地点' }]}
          >
            <Input 
              placeholder='输入地点' 
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item 
            name='date' 
            label='日期'
            rules={[{ required: true, message: '请选择日期' }]}
          >
            <Input 
              type='date' 
              readOnly 
              disabled
              style={{ 
                color: '#333', 
                opacity: 1
              }}
            />
          </Form.Item>
          <div style={{ padding: '0 12px 4px' }}>
            <Space wrap>
              {Array.isArray(tags) ? tags.map((tag, index) => (
                <Tag
                  key={index}
                  color='primary'
                  onClick={() => removeTag(index)}
                  style={{ fontSize: '14px', padding: '3px 6px', margin: '2px' }}
                >
                  {tag}
                </Tag>
              )) : null}
              <Tag
                color='default'
                onClick={() => setTagVisible(true)}
                style={{ 
                  fontSize: '14px', 
                  padding: '4px 10px', 
                  margin: '2px',
                  height: '28px',
                  lineHeight: '20px',
                  borderRadius: '4px'
                }}
              >
                + 添加标记
              </Tag>
            </Space>
          </div>
          <div style={{ 
            padding: '0 12px 8px', 
            fontSize: '13px', 
            color: '#888', 
            textAlign: 'left' 
          }}>
            其它信息可以通过添加标记来记录。
          </div>

        </Form>
      )}

      {/* 固定在底部的按钮区域 */}
      {!loading && (
        <div style={{ 
          position: 'fixed', 
          bottom: 10, 
          left: 0, 
          right: 0, 
          padding: '12px 16px', 
          background: '#fff', 
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)',
          zIndex: 100,
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          {editingId ? (
            <>
              <Button 
                onClick={() => {
                  localStorage.removeItem('editingId');
                  navigate('/list');
                }}
                style={{ 
                  flex: 1,
                  marginRight: '4px',
                  height: '44px', 
                  fontSize: '15px'
                }}
              >
                取消
              </Button>
              <Button
                color='default'
                onClick={() => navigate('/list')}
                style={{ 
                  flex: 1,
                  marginRight: '4px',
                  height: '44px', 
                  fontSize: '15px'
                }}
              >
                查看
              </Button>
              <Button 
                color='primary' 
                loading={loading}
                onClick={() => form.submit()}
                style={{ 
                  flex: 1,
                  height: '44px', 
                  fontSize: '15px'
                }}
              >
                保存
              </Button>
            </>
          ) : (
            <>
              <Button
                color='default'
                onClick={() => navigate('/list')}
                style={{ 
                  flex: 1,
                  marginRight: '28px',
                  height: '44px', 
                  fontSize: '20px'
                }}
              >
                查看采集记录
              </Button>
              <Button 
                color='primary' 
                loading={loading}
                onClick={() => form.submit()}
                style={{ 
                  flex: 1,
                  height: '44px', 
                  fontSize: '20px'
                }}
              >
                保存
              </Button>
            </>
          )}
        </div>
      )}

      {/* 添加标签弹窗 */}
      <Popup
        visible={tagVisible}
        onMaskClick={() => setTagVisible(false)}
        bodyStyle={{
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          padding: '20px',
          width: '100%',
          minHeight: '200px',
          maxWidth: '100vw',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ width: '100%', padding: '10px 12px 12px 12px' }}>
          <div style={{ marginBottom: '20px', fontWeight: 'bold', fontSize: '18px' }}>添加新标记</div>
          <Input
            placeholder='请输入标记名称'
            value={newTag}
            onChange={setNewTag}
            onEnterPress={addTag}
            style={{ 
              marginBottom: '20px',
              '--font-size': '16px',
              '--placeholder-color': '#999',
              '--height': '48px'
            } as any}
          />
          <div style={{ 
            marginTop: '20px', 
            display: 'flex', 
            justifyContent: 'space-between', 
            gap: '15px',
            width: '100%'
          }}>
            <Button 
              style={{ 
                flex: '1', 
                height: '48px',
                fontSize: '16px',
                borderRadius: '8px',
                maxWidth: 'calc(50% - 7.5px)'
              }} 
              onClick={() => setTagVisible(false)}
            >
              取消
            </Button>
            <Button 
              color='primary' 
              onClick={addTag}
              style={{ 
                flex: '1', 
                height: '48px',
                fontSize: '16px',
                borderRadius: '8px',
                maxWidth: 'calc(50% - 7.5px)',
                marginRight: '20px'
              }}
            >
              确定
            </Button>
          </div>
        </div>
      </Popup>
      
      {/* 视频预览弹窗 */}
      <Popup
        visible={previewVisible}
        onMaskClick={() => setPreviewVisible(false)}
        bodyStyle={{
          height: '100vh',
          width: '100vw',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden'
        }}
      >
        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {previewUrl && (
            <video
              src={previewUrl}
              controls
              autoPlay
              playsInline
              webkit-playsinline="true"
              x5-video-player-type="h5"
              x5-video-player-fullscreen="true"
              style={{
                width: '90%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}
          <Button
            style={{
              position: 'absolute',
              bottom: 30,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: '#fff',
              zIndex: 1,
              borderRadius: '20px',
              fontSize: '16px',
              paddingLeft: '25px',
              paddingRight: '25px'
            }}
            onClick={() => setPreviewVisible(false)}
          >
            关闭
          </Button>
        </div>
      </Popup>
      
      {/* 图片查看器 - 使用ImageViewer组件 */}
      <ImageViewer.Multi
        images={mediaItems
          .filter(item => item.type === 'image')
          .map(item => item.url)
        }
        visible={imageViewerVisible}
        defaultIndex={currentImageIndex}
        onClose={() => setImageViewerVisible(false)}
        renderFooter={() => (
          <div
            style={{
              color: '#fff',
              textAlign: 'center',
              padding: '12px 0',
              fontSize: '14px'
            }}
          >
            支持双指放大缩小和滑动查看
          </div>
        )}
      />
      
      {/* 商品选择弹窗 */}
      <Popup
        visible={showProductSelector}
        onMaskClick={() => setShowProductSelector(false)}
        bodyStyle={{
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          maxHeight: '70vh',
          padding: '14px',
          overflow: 'auto',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ padding: '16px 16px 16px 16px' }}>
          <div style={{ marginBottom: 5, fontWeight: 'bold', fontSize: 20 }}>选择或输入商品名称</div>
          
          {/* 搜索/输入框 */}
          <SearchBar
            placeholder='搜索或输入自定义商品名称'
            value={productSearchValue}
            onChange={setProductSearchValue}
            style={{ 
              '--border-radius': '4px', 
              width: '100%', 
              marginBottom: 2,
              '--height': '44px',
              fontSize: '16px'
            }}
            onSearch={(val) => {
              // 如果用户点击搜索，且没有匹配的选项，则使用输入的值
              if (val && getFilteredProducts().length === 0) {
                handleSelectProductName(val);
              }
            }}
          />
          
          {/* 商品列表 */}
          {productNameLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <DotLoading color='primary' />
              <span style={{ marginLeft: 8 }}>加载中...</span>
            </div>
          ) : (
            <>
              {getFilteredProducts().length > 0 ? (
                <List style={{ '--border-top': 'none', '--border-bottom': 'none', '--font-size': '18px' }}>
                  {getFilteredProducts().map((item, index) => (
                    <List.Item
                      key={index}
                      onClick={() => handleSelectProductName(item.product_name)}
                      arrow={false}
                      extra={
                        <div style={{ 
                          fontSize: '26px', 
                          color: item.price_num === 0 ? '#ff4d4f' : '#666', 
                          backgroundColor: '#e6f9ff', 
                          padding: '2px 6px', 
                          borderRadius: '10px' 
                        }}>
                          {item.price_num}
                        </div>
                      }
                    >
                      {item.product_name}
                    </List.Item>
                  ))}
                </List>
              ) : (
                <div style={{ padding: '12px 0' }}>
                  <div style={{ marginBottom: 10, color: '#999', fontSize: 14, textAlign: 'center' }}>
                    没有找到匹配的商品
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* 底部按钮 */}
          <div style={{ 
            marginTop: 22, 
            marginBottom: 22,
            display: 'flex', 
            justifyContent: productSearchValue ? 'space-between' : 'center', 
            gap: 10,
            width: '100%'
          }}>
            <Button 
              style={{ 
                width: productSearchValue ? '48%' : '40%', 
                flex: productSearchValue ? 1 : 'unset',
                height: '46px',
                fontSize: '15px'
              }} 
              onClick={() => setShowProductSelector(false)}
            >
              取消
            </Button>
            {productSearchValue && (
              <Button 
                color='primary' 
                style={{ 
                  width: '48%', 
                  flex: 1,
                  height: '46px',
                  fontSize: '15px'
                }}
                onClick={() => handleSelectProductName(productSearchValue)}
              >
                确定
              </Button>
            )}
          </div>
        </div>
      </Popup>

      {/* 相似商品选择弹窗 */}
      <Popup
        visible={showSimilarProductSelector}
        onMaskClick={() => setShowSimilarProductSelector(false)}
        bodyStyle={{
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          maxHeight: '70vh',
          padding: '14px',
          overflow: 'auto',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ padding: '16px 16px 16px 16px' }}>
          <div style={{ marginBottom: 10, fontWeight: 'bold', fontSize: 20 }}>选择相似商品名称</div>
          
          {/* 相似商品列表 */}
          {similarProductOptions.length > 0 ? (
            <List style={{ '--border-top': 'none', '--border-bottom': 'none', '--font-size': '18px' }}>
              {similarProductOptions.map((item, index) => (
                <List.Item
                  key={index}
                  onClick={() => {
                    form.setFieldsValue({ similarProductName: item });
                    setShowSimilarProductSelector(false);
                  }}
                  arrow={false}
                >
                  {item}
                </List.Item>
              ))}
            </List>
          ) : (
            <div style={{ padding: '12px 0' }}>
              <div style={{ marginBottom: 10, color: '#999', fontSize: 14, textAlign: 'center' }}>
                暂无历史记录
              </div>
            </div>
          )}
          
          {/* 底部按钮 */}
          <div style={{ 
            marginTop: 22, 
            marginBottom: 22,
            display: 'flex', 
            justifyContent: 'center', 
            gap: 10,
            width: '100%'
          }}>
            <Button 
              style={{ 
                width: '40%', 
                height: '46px',
                fontSize: '15px'
              }} 
              onClick={() => setShowSimilarProductSelector(false)}
            >
              取消
            </Button>
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default PriceForm; 